import React, { useMemo, useState } from 'react';
import { Button, Card, Col, DatePicker, Input, Row, Select, Space, Table, Tag, Typography } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import orderReviewChecklistService from '@/services/orderReviewChecklist.service';
import { PendingApproverText } from '@/components/approval/PendingApproverText';
import { usePendingApproversMap } from '@/hooks/usePendingApproversMap';
import { useOpportunities } from '@/hooks/useOpportunities';
import { listAssignableSalesTeams } from '@/services/salesTeam.service';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const OrderReviewChecklistRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | undefined>();
  const [approvalStatus, setApprovalStatus] = useState<string | undefined>();
  const [risk, setRisk] = useState<string | undefined>();
  const [range, setRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [teamFilter, setTeamFilter] = useState<string | undefined>();

  const { data: salesTeams = [] } = useQuery({
    queryKey: ['sales-teams-assignable'],
    queryFn: listAssignableSalesTeams,
  });

  const filters = useMemo(
    () => ({
      status,
      approval_status: approvalStatus,
      risk_level: risk,
      date_from: range?.[0]?.format('YYYY-MM-DD'),
      date_to: range?.[1]?.format('YYYY-MM-DD'),
      search: search || undefined,
      sales_team_id: teamFilter || undefined,
    }),
    [status, approvalStatus, risk, range, search, teamFilter]
  );

  const { data, isLoading } = useQuery({
    queryKey: ['order-review-checklist', 'list', filters],
    queryFn: () => orderReviewChecklistService.list(filters),
  });
  const { data: opportunities = [] } = useOpportunities();
  const opportunityById = useMemo(
    () =>
      new Map(
        opportunities.map((o: any) => [
          o.id,
          `${o.opportunity_number || ''}${o.opportunity_name ? ` — ${o.opportunity_name}` : ''}`.trim(),
        ])
      ),
    [opportunities]
  );

  const rows = data?.data || [];

  const pendingInstanceIds = useMemo(
    () =>
      rows
        .filter((r) => r.approval_instance_id && r.approval_status === 'Pending')
        .map((r) => r.approval_instance_id as string),
    [rows]
  );
  const { labels: pendingApproverLabels } = usePendingApproversMap(pendingInstanceIds);

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Row justify="space-between" align="middle">
            <Col>
              <h2 style={{ margin: 0 }}>Enquiry Checklist Register</h2>
            </Col>
            <Col>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/growth/opportunities')}>
                Create from Opportunity
              </Button>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Input
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                prefix={<SearchOutlined />}
                placeholder="Search ORC no/customer/opportunity"
              />
            </Col>
            <Col xs={24} md={4}>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Status"
                value={status}
                onChange={setStatus}
                options={[
                  { value: 'Draft', label: 'Draft' },
                  { value: 'Submitted', label: 'Submitted' },
                  { value: 'Under Review', label: 'Under Review' },
                  { value: 'Approved', label: 'Approved' },
                  { value: 'Rejected', label: 'Rejected' },
                  { value: 'Returned for Correction', label: 'Returned for Correction' },
                  { value: 'Cancelled', label: 'Cancelled' },
                ]}
              />
            </Col>
            <Col xs={24} md={4}>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Approval"
                value={approvalStatus}
                onChange={setApprovalStatus}
                options={[
                  { value: 'Pending', label: 'Pending' },
                  { value: 'In Progress', label: 'In Progress' },
                  { value: 'Approved', label: 'Approved' },
                  { value: 'Rejected', label: 'Rejected' },
                ]}
              />
            </Col>
            <Col xs={24} md={4}>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Risk"
                value={risk}
                onChange={setRisk}
                options={['Low', 'Medium', 'High'].map((x) => ({ value: x, label: x }))}
              />
            </Col>
            <Col xs={24} md={4}>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Sales team"
                value={teamFilter}
                onChange={setTeamFilter}
                options={salesTeams.map((t) => ({ value: t.id, label: t.team_name }))}
              />
            </Col>
            <Col xs={24} md={8}>
              <RangePicker style={{ width: '100%' }} value={range} onChange={setRange} />
            </Col>
          </Row>

          <Table
            rowKey="id"
            loading={isLoading}
            dataSource={rows}
            pagination={{ pageSize: 20 }}
            columns={[
              {
                title: 'ORC #',
                dataIndex: 'orc_number',
                render: (v: string, r: any) => (
                  <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/growth/order-review-checklists/${r.id}`)}>
                    {v}
                  </Button>
                ),
              },
              {
                title: 'Opportunity',
                dataIndex: 'opportunity_id',
                render: (v: string) => (v ? opportunityById.get(v) || '—' : '—'),
              },
              { title: 'Customer', dataIndex: 'customer_name', render: (v: string) => v || '—' },
              { title: 'Sales team', dataIndex: 'sales_team_name', render: (v: string) => v || '—' },
              { title: 'Risk', dataIndex: 'overall_risk_rating', render: (v: string) => v || '—' },
              { title: 'Status', dataIndex: 'review_status', render: (v: string) => (v ? <Tag>{v}</Tag> : '—') },
              {
                title: 'Approval',
                dataIndex: 'approval_status',
                render: (v: string, r: { approval_instance_id?: string | null }) =>
                  v ? (
                    <div>
                      <Tag color="blue">{v}</Tag>
                      {r.approval_instance_id && v === 'Pending' ? (
                        <PendingApproverText compact pendingApproverLabel={pendingApproverLabels[r.approval_instance_id]} />
                      ) : null}
                    </div>
                  ) : (
                    '—'
                  ),
              },
              { title: 'Review date', dataIndex: 'review_date', render: (v: string) => (v ? dayjs(v).format('DD MMM YYYY') : '—') },
            ]}
          />
          <Text type="secondary">Tip: Use Opportunity detail page to create ORC directly against an opportunity.</Text>
        </Space>
      </Card>
    </div>
  );
};

export default OrderReviewChecklistRegisterPage;

